import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Activity, Users, ShoppingCart, Clock, DollarSign,
    ChefHat, RefreshCw, AlertTriangle, Eye, Utensils,
    CreditCard, Flame, CheckCircle, Timer, TrendingUp
} from 'lucide-react';

const REFRESH_INTERVAL = 5000; // 5 secondes pour temps réel

const PRIORITY_COLORS = {
    urgent: 'bg-red-500 animate-pulse',
    normal: 'bg-yellow-500',
    new: 'bg-blue-500'
};

// Format time ago
const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        
        if (diff < 60) return "À l'instant";
        if (diff < 3600) return `${Math.floor(diff / 60)} min`;
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const LiveMonitoringPage = () => {
    const { api, user } = useAuth();
    const [liveData, setLiveData] = useState(null);
    const [liveFeed, setLiveFeed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const intervalRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            const [statsRes, feedRes] = await Promise.all([
                api().get('/pos/supervision/live-stats'),
                api().get('/pos/admin/dashboard/live-feed?filter_type=all&limit=30')
            ]);
            setLiveData(statsRes.data);
            setLiveFeed(feedRes.data?.activities || []);
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching live data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
        
        if (autoRefresh) {
            intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL);
        }
        
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [fetchData, autoRefresh]);

    const formatCFA = (amount) => `${(amount || 0).toLocaleString('fr-FR')} F`;

    // Check access
    const allowedRoles = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'];
    if (!allowedRoles.includes(user?.role)) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900">
                <Card className="p-8 bg-gray-800 border-gray-700">
                    <div className="text-center">
                        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white">Accès refusé</h2>
                        <p className="text-gray-400">Cette page est réservée aux managers.</p>
                    </div>
                </Card>
            </div>
        );
    }

    if (loading && !liveData) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900">
                <RefreshCw className="h-12 w-12 animate-spin text-green-400" />
            </div>
        );
    }

    // Activity icon mapping
    const getActivityIcon = (type) => {
        switch (type) {
            case 'payment': return <CreditCard className="w-4 h-4" />;
            case 'ready': return <CheckCircle className="w-4 h-4" />;
            case 'cooking': return <Flame className="w-4 h-4" />;
            case 'kitchen': return <ChefHat className="w-4 h-4" />;
            case 'new_order': return <ShoppingCart className="w-4 h-4" />;
            default: return <Activity className="w-4 h-4" />;
        }
    };

    const getActivityColor = (color) => {
        const colors = {
            green: 'bg-green-500/20 text-green-400',
            emerald: 'bg-emerald-500/20 text-emerald-400',
            orange: 'bg-orange-500/20 text-orange-400',
            blue: 'bg-blue-500/20 text-blue-400',
            violet: 'bg-violet-500/20 text-violet-400',
            amber: 'bg-amber-500/20 text-amber-400',
            red: 'bg-red-500/20 text-red-400'
        };
        return colors[color] || 'bg-gray-500/20 text-gray-400';
    };

    const hasActivity = liveFeed.length > 0 || liveData?.commandes_cuisine > 0 || liveData?.commandes_pretes > 0;

    return (
        <div className="min-h-screen bg-gray-900 text-white p-3 sm:p-4 lg:p-6" data-testid="live-monitoring-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                        <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold">Supervision en Direct</h1>
                        <p className="text-gray-400 text-xs sm:text-sm">
                            Mise à jour: {lastUpdate?.toLocaleTimeString('fr-FR')}
                            {autoRefresh && <span className="ml-2 text-green-400">• Live</span>}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant={autoRefresh ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className={autoRefresh ? "bg-green-600 hover:bg-green-700" : "border-gray-600"}
                    >
                        <Activity className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-pulse' : ''}`} />
                        {autoRefresh ? 'Auto ON' : 'Auto OFF'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchData} className="border-gray-600">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Global Status Badge */}
            {liveData && (
                <div className="mb-4">
                    <Badge 
                        className={`text-sm px-3 py-1 ${
                            liveData.global_status === 'red' ? 'bg-red-500 animate-pulse' :
                            liveData.global_status === 'orange' ? 'bg-orange-500' :
                            'bg-green-500'
                        }`}
                    >
                        {liveData.global_message}
                    </Badge>
                </div>
            )}

            {/* KPI Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Card className="bg-gradient-to-br from-green-600/80 to-green-800/80 border-0">
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-100 text-xs">CA Jour</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.chiffre_affaires_formatted || '0 F'}</p>
                            </div>
                            <DollarSign className="h-6 w-6 text-green-300/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-600/80 to-blue-800/80 border-0">
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-xs">Commandes</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.commandes_jour || 0}</p>
                            </div>
                            <ShoppingCart className="h-6 w-6 text-blue-300/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className={`border-0 ${liveData?.tables_status === 'red' ? 'bg-gradient-to-br from-red-600/80 to-red-800/80' : liveData?.tables_status === 'orange' ? 'bg-gradient-to-br from-orange-600/80 to-orange-800/80' : 'bg-gradient-to-br from-purple-600/80 to-purple-800/80'}`}>
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-xs">Tables</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.tables_ouvertes || 0}</p>
                            </div>
                            <Utensils className="h-6 w-6 text-purple-300/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className={`border-0 ${liveData?.kitchen_status === 'red' ? 'bg-gradient-to-br from-red-600/80 to-red-800/80 animate-pulse' : liveData?.kitchen_status === 'orange' ? 'bg-gradient-to-br from-orange-600/80 to-orange-800/80' : 'bg-gradient-to-br from-yellow-600/80 to-yellow-800/80'}`}>
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-yellow-100 text-xs">En cuisine</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.commandes_cuisine || 0}</p>
                            </div>
                            <ChefHat className="h-6 w-6 text-yellow-300/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className={`border-0 ${liveData?.ready_status === 'red' ? 'bg-gradient-to-br from-red-600/80 to-red-800/80' : 'bg-gradient-to-br from-emerald-600/80 to-emerald-800/80'}`}>
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-emerald-100 text-xs">Prêtes</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.commandes_pretes || 0}</p>
                            </div>
                            <CheckCircle className="h-6 w-6 text-emerald-300/50" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-cyan-600/80 to-cyan-800/80 border-0">
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-cyan-100 text-xs">Temps moy.</p>
                                <p className="text-lg sm:text-xl font-bold">{liveData?.temps_moyen_cuisine || 0} min</p>
                            </div>
                            <Timer className="h-6 w-6 text-cyan-300/50" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Live Activity Feed */}
                <Card className="bg-gray-800 border-gray-700">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base sm:text-lg flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <Eye className="h-5 w-5 text-green-400" />
                                Activité en Direct
                            </span>
                            {autoRefresh && (
                                <span className="flex items-center gap-1 text-xs text-green-400">
                                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                                    Live
                                </span>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <ScrollArea className="h-[400px] sm:h-[500px]">
                            <div className="p-3 space-y-2">
                                {liveFeed.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500">
                                        <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                        <p className="text-sm">Aucune activité récente</p>
                                        <p className="text-xs text-gray-600 mt-1">Les nouvelles activités apparaîtront ici</p>
                                    </div>
                                ) : (
                                    liveFeed.map((activity, i) => (
                                        <div 
                                            key={i}
                                            className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                                        >
                                            <div className={`p-2 rounded-full ${getActivityColor(activity.color)}`}>
                                                {getActivityIcon(activity.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-white truncate">{activity.message}</p>
                                                <p className="text-xs text-gray-400 truncate">{activity.detail}</p>
                                            </div>
                                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                                {formatTimeAgo(activity.time)}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Alerts and Critical Items */}
                <Card className="bg-gray-800 border-gray-700">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-400" />
                            Alertes & Points d'attention
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="h-[400px] sm:h-[500px]">
                            <div className="space-y-3">
                                {/* No activity message */}
                                {!hasActivity && (
                                    <div className="text-center py-8 text-gray-500">
                                        <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
                                        <p className="text-green-400 font-medium">Tout est calme</p>
                                        <p className="text-xs text-gray-600 mt-1">Aucune alerte en cours</p>
                                    </div>
                                )}

                                {/* Kitchen alerts */}
                                {liveData?.commandes_cuisine > 0 && (
                                    <div className={`p-3 rounded-lg ${liveData.kitchen_status === 'red' ? 'bg-red-500/20 border border-red-500/50' : 'bg-yellow-500/20 border border-yellow-500/50'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <ChefHat className={`h-5 w-5 ${liveData.kitchen_status === 'red' ? 'text-red-400' : 'text-yellow-400'}`} />
                                            <span className="font-medium">Cuisine</span>
                                        </div>
                                        <p className="text-sm">
                                            <span className="font-bold text-lg">{liveData.commandes_cuisine}</span> commande(s) en préparation
                                        </p>
                                        {liveData.temps_moyen_cuisine > 10 && (
                                            <p className="text-xs text-amber-400 mt-1">
                                                ⚠ Temps moyen élevé: {liveData.temps_moyen_cuisine} min
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Ready orders waiting */}
                                {liveData?.commandes_pretes > 0 && (
                                    <div className={`p-3 rounded-lg ${liveData.ready_status === 'red' ? 'bg-red-500/20 border border-red-500/50 animate-pulse' : 'bg-emerald-500/20 border border-emerald-500/50'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <CheckCircle className="h-5 w-5 text-emerald-400" />
                                            <span className="font-medium">Commandes Prêtes</span>
                                        </div>
                                        <p className="text-sm">
                                            <span className="font-bold text-lg">{liveData.commandes_pretes}</span> commande(s) à servir
                                        </p>
                                        {liveData.ready_status === 'red' && (
                                            <p className="text-xs text-red-400 mt-1">
                                                ⚠ Trop de commandes en attente de service!
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Tables status */}
                                {liveData?.tables_status !== 'green' && liveData?.tables_ouvertes > 0 && (
                                    <div className={`p-3 rounded-lg ${liveData.tables_status === 'red' ? 'bg-red-500/20 border border-red-500/50' : 'bg-orange-500/20 border border-orange-500/50'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Utensils className="h-5 w-5 text-orange-400" />
                                            <span className="font-medium">Salle</span>
                                        </div>
                                        <p className="text-sm">
                                            <span className="font-bold text-lg">{liveData.tables_ouvertes}</span> tables occupées
                                        </p>
                                        <p className="text-xs text-orange-400 mt-1">
                                            {liveData.tables_status === 'red' ? '⚠ Salle saturée!' : '⚠ Charge élevée'}
                                        </p>
                                    </div>
                                )}

                                {/* No sale alert */}
                                {liveData?.minutes_since_last_sale > 30 && (
                                    <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/50">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Clock className="h-5 w-5 text-amber-400" />
                                            <span className="font-medium">Ventes</span>
                                        </div>
                                        <p className="text-sm">
                                            Aucune vente depuis <span className="font-bold">{liveData.minutes_since_last_sale}</span> minutes
                                        </p>
                                    </div>
                                )}

                                {/* Caisses status */}
                                {liveData?.caisses_status !== 'green' && (
                                    <div className={`p-3 rounded-lg ${liveData.caisses_status === 'red' ? 'bg-red-500/20 border border-red-500/50' : 'bg-orange-500/20 border border-orange-500/50'}`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <CreditCard className="h-5 w-5 text-red-400" />
                                            <span className="font-medium">Caisses</span>
                                        </div>
                                        <p className="text-sm">
                                            <span className="font-bold text-lg">{liveData.caisses_actives}</span> caisse(s) active(s)
                                        </p>
                                        {liveData.caisses_errors > 0 && (
                                            <p className="text-xs text-red-400 mt-1">
                                                ⚠ {liveData.caisses_errors} erreur(s) détectée(s)
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Recent critical activities from feed */}
                                {liveData?.recent_activity?.filter(a => a.critical).map((alert, i) => (
                                    <div key={i} className="p-3 rounded-lg bg-red-500/20 border border-red-500/50 animate-pulse">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-5 w-5 text-red-400" />
                                            <span className="text-sm">{alert.message}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default LiveMonitoringPage;
